import {
  Box,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { H6 } from 'components/Typography';
import { Clock, Search, TrendingUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const SearchBar = ({
  placeholder = '검색어를 입력하세요',
  recentSearches = [],
  popularSearches = [
    '2차민생지원금',
    '인기검색어',
    '인기검색어',
    '인기검색어',
    '인기검색어',
    '인기검색어',
    '인기검색어',
    '인기검색어',
    '인기검색어',
    '인기검색어',
  ],
  onSearchResult,
  ...props
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchHistory, setSearchHistory] = useState(() => {
    // Load search history from localStorage on component mount
    try {
      const saved = localStorage.getItem('searchHistory');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Use search history if available, otherwise use provided recent searches or defaults
  const displayRecentSearches =
    searchHistory.length > 0
      ? searchHistory
      : recentSearches.length > 0
      ? recentSearches
      : [
          '민생지원금 어떻게 받나요',
          '민생지원금 어떻게 받나요',
          '민생지원금 어떻게 받나요',
          '민생지원금 어떻게 받나요',
        ];

  const handleSearchChange = event => {
    setSearchTerm(event.target.value);
  };

  const performSearch = term => {
    if (term.trim()) {
      // Add to search history
      setSearchHistory(prev => {
        const newHistory = [term, ...prev.filter(item => item !== term)].slice(
          0,
          10
        );
        // Save to localStorage
        try {
          localStorage.setItem('searchHistory', JSON.stringify(newHistory));
        } catch (error) {
          console.warn('Failed to save search history:', error);
        }
        return newHistory;
      });

      // Close dropdown
      setIsDropdownOpen(false);

      // Call external callback if provided
      if (onSearchResult) {
        onSearchResult(term);
      } else {
        // Default search behavior - you can customize this
        console.log('Searching for:', term);
        // Here you could implement actual search logic, API calls, etc.
      }
    }
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      performSearch(searchTerm);
    }
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleInputBlur = event => {
    // Check if the blur is due to clicking on the dropdown
    if (
      dropdownRef.current &&
      dropdownRef.current.contains(event.relatedTarget)
    ) {
      return;
    }
    setIsDropdownOpen(false);
  };

  const handleSearchItemClick = term => {
    setSearchTerm(term);
    performSearch(term);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box
      ref={searchRef}
      sx={{ width: '100%', position: 'relative', ...props.sx }}
    >
      <TextField
        fullWidth
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleSearchChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyPress={handleKeyPress}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Box
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px',
                  borderRadius: '50%',
                  '&:hover': {
                    backgroundColor: 'rgba(66, 133, 244, 0.1)',
                  },
                }}
                onClick={() => {
                  performSearch(searchTerm);
                }}
              >
                <Search size={20} color={theme.palette.primary.mainBlue} />
              </Box>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '50px',
            backgroundColor: '#f8f9fa',
            border: '1.5px solid #4285f4',
            '& fieldset': {
              border: 'none',
            },
          },
          '& .MuiInputBase-input': {
            padding: '12px 16px',
            fontSize: '14px',
          },
        }}
      />

      {/* Search Dropdown Panel */}
      {isDropdownOpen && (
        <Paper
          ref={dropdownRef}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #e1e1e1',
            backgroundColor: 'white',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', minHeight: '200px' }}>
            {/* Recent Searches Column */}
            <Box sx={{ flex: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Clock
                  size={16}
                  color={theme.palette.primary.mainBlue}
                  style={{ marginRight: '8px' }}
                />
                <H6 sx={{ color: theme.palette.primary.mainBlue }}>
                  최근 검색어
                </H6>
              </Box>
              <List sx={{ p: 0 }}>
                {displayRecentSearches.map((search, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      p: '3px 0',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                      },
                    }}
                    onClick={() => handleSearchItemClick(search)}
                  >
                    <ListItemText
                      primary={search}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '14px',
                          color: '#000',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Divider */}
            <Divider orientation="vertical" flexItem />

            {/* Popular Searches Column */}
            <Box sx={{ flex: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp
                  size={16}
                  color="#4285f4"
                  style={{ marginRight: '8px' }}
                />
                <H6 sx={{ color: theme.palette.primary.mainBlue }}>
                  인기 검색어
                </H6>
              </Box>
              <List sx={{ p: 0 }}>
                {popularSearches.map((search, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      p: '3px 0',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                      },
                    }}
                    onClick={() => handleSearchItemClick(search)}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                index < 3
                                  ? theme.palette.primary.mainBlue
                                  : theme.palette.text.secondary,
                              fontWeight: '500',
                              minWidth: '20px',
                              mr: 1,
                            }}
                          >
                            {index + 1}.
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#000',
                              fontSize: '14px',
                            }}
                          >
                            {search}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default SearchBar;
